import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Upload as UploadIcon,
  PlayArrow as ProcessIcon,
  FileCopy as FileIcon,
  Link as LinkIcon,
  Storage as StorageIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import { clearError, processEntity, deleteEntity } from '../store/slices/entitySlice';
import { showSuccess, showError, showInfo } from '../store/slices/toastSlice';
import { Entity, EntityStatus } from '../types';
import { entityApi } from '../services/entityApi';

const EntityDetail: React.FC = () => {
  const { entityId } = useParams<{ entityId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { entities, isLoading, error } = useAppSelector((state) => state.entities);

  const [currentEntity, setCurrentEntity] = useState<Entity | null>(null);
  const [isLoadingEntity, setIsLoadingEntity] = useState(false);
  const [entityError, setEntityError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (entityId) {
      // First try to find in the existing entities list
      const entity = entities.find(e => e.id === entityId || e.entity_id === entityId);
      if (entity) {
        setCurrentEntity(entity);
        setEntityError(null);
      } else {
        // If not found in list, fetch it individually
        setIsLoadingEntity(true);
        setEntityError(null);
        
        entityApi.getEntity(entityId)
          .then((fetchedEntity) => {
            setCurrentEntity(fetchedEntity);
            setIsLoadingEntity(false);
          })
          .catch((error) => {
            console.error('Error fetching entity:', error);
            setEntityError(error.message || 'Failed to fetch entity');
            setIsLoadingEntity(false);
          });
      }
    }
  }, [entityId, entities]);

  useEffect(() => {
    if (error) {
      dispatch(showError({
        title: 'Error',
        message: error,
        duration: 5000,
      }));
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const getStatusColor = (status: EntityStatus) => {
    switch (status) {
      case EntityStatus.ACTIVE:
        return 'success';
      case EntityStatus.PROCESSING:
        return 'warning';
      case EntityStatus.INACTIVE:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: EntityStatus) => {
    switch (status) {
      case EntityStatus.ACTIVE:
        return <CheckCircleIcon color="success" />;
      case EntityStatus.PROCESSING:
        return <ScheduleIcon color="warning" />;
      case EntityStatus.INACTIVE:
        return <ErrorIcon color="error" />;
      default:
        return <WarningIcon />;
    }
  };

  const handleProcess = async () => {
    if (!currentEntity) return;
    
    setIsProcessing(true);
    try {
      await dispatch(processEntity(currentEntity.id || currentEntity.entity_id)).unwrap();
      
      // Update the current entity with the processed data
      const updatedEntity = entities.find(e => 
        e.id === currentEntity.id || e.entity_id === currentEntity.entity_id
      );
      if (updatedEntity) {
        setCurrentEntity(updatedEntity);
      }
      
      dispatch(showSuccess({
        title: 'Success',
        message: 'Knowledge base processed successfully!',
        duration: 3000,
      }));
    } catch (error: any) {
      dispatch(showError({
        title: 'Processing Failed',
        message: error.message || 'Failed to process knowledge base',
        duration: 5000,
      }));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUploadFiles = () => {
    dispatch(showInfo({
      title: 'Coming Soon',
      message: 'File upload functionality will be implemented soon.',
      duration: 4000,
    }));
  };

  const handleDelete = async () => {
    if (!currentEntity) return;
    
    setIsDeleting(true);
    try {
      await dispatch(deleteEntity(currentEntity.id || currentEntity.entity_id)).unwrap();
      
      dispatch(showSuccess({
        title: 'Success',
        message: 'Knowledge base deleted successfully!',
        duration: 3000,
      }));
      
      navigate('/admin/entities');
    } catch (error: any) {
      dispatch(showError({
        title: 'Deletion Failed',
        message: error.message || 'Failed to delete knowledge base',
        duration: 5000,
      }));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (isLoading || isLoadingEntity) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (entityError) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/entities')}
            sx={{ mr: 2 }}
          >
            Back to Knowledge Bases
          </Button>
        </Box>
        <Alert severity="error">
          {entityError === 'Entity not found' 
            ? 'Knowledge base not found. It may have been deleted or the ID is incorrect.'
            : entityError
          }
        </Alert>
      </Box>
    );
  }

  if (!currentEntity) {
    return (
      <Box>
        <Box display="flex" alignItems="center" mb={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/entities')}
            sx={{ mr: 2 }}
          >
            Back to Knowledge Bases
          </Button>
        </Box>
        <Alert severity="error">Knowledge base not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/entities')}
          sx={{ mr: 2 }}
        >
          Back to Knowledge Bases
        </Button>
        <Typography variant="h4" component="h1">
          {currentEntity.name}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} md={8}>
          {/* Entity Information */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {getStatusIcon(currentEntity.status || EntityStatus.ACTIVE)}
                    <Chip
                      label={currentEntity.status || 'Active'}
                      color={getStatusColor(currentEntity.status || EntityStatus.ACTIVE)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Processing Status
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {currentEntity.is_processed ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <ScheduleIcon color="warning" />
                    )}
                    <Chip
                      label={currentEntity.is_processed ? 'Processed' : 'Not Processed'}
                      color={currentEntity.is_processed ? 'success' : 'warning'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Description
                  </Typography>
                  <Typography variant="body1" mt={1}>
                    {currentEntity.description || 'No description available'}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Knowledge Base Content */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Knowledge Base Content
              </Typography>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center">
                    <DescriptionIcon sx={{ mr: 1 }} />
                    <Typography variant="subtitle1">
                      View Knowledge Base Data
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 2, 
                      backgroundColor: 'background.paper',
                      border: '1px solid',
                      borderColor: (t) => t.palette.divider,
                      maxHeight: 400, 
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {currentEntity.data || 'No content available'}
                  </Paper>
                  <Box mt={2}>
                    <Typography variant="body2" color="text.secondary">
                      Content Length: {currentEntity.data ? currentEntity.data.length : 0} characters
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>

          {/* Statistics */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Statistics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.paper', border: '1px solid', borderColor: (t) => t.palette.divider }}>
                    <FileIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" component="div">
                      {currentEntity.file_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Files
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.paper', border: '1px solid', borderColor: (t) => t.palette.divider }}>
                    <LinkIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" component="div">
                      {currentEntity.url_count}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      URLs
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Paper elevation={0} sx={{ p: 2, textAlign: 'center', backgroundColor: 'background.paper', border: '1px solid', borderColor: (t) => t.palette.divider }}>
                    <StorageIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography variant="h4" component="div">
                      {currentEntity.is_processed ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Processed
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Actions
              </Typography>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  onClick={handleUploadFiles}
                >
                  Upload Files
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ProcessIcon />}
                  onClick={handleProcess}
                  disabled={currentEntity.is_processed || isProcessing}
                >
                  {isProcessing ? 'Processing...' : 'Process Knowledge Base'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Knowledge Base'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Metadata */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Metadata
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Created"
                    secondary={new Date(currentEntity.created_at).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <ScheduleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Updated"
                    secondary={new Date(currentEntity.updated_at).toLocaleDateString()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <StorageIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Owner ID"
                    secondary={currentEntity.user_id}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <List dense>
                <ListItem button onClick={handleUploadFiles}>
                  <ListItemIcon>
                    <UploadIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Upload Files" />
                </ListItem>
                <ListItem button onClick={handleProcess} disabled={currentEntity.is_processed}>
                  <ListItemIcon>
                    <ProcessIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Process Knowledge Base" />
                </ListItem>
                <ListItem button onClick={() => setDeleteDialogOpen(true)}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Delete Knowledge Base" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Knowledge Base</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{currentEntity.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntityDetail;
